/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {mergeRegister} from '@lexical/utils';
import {
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  COMMAND_PRIORITY_EDITOR,
  createCommand,
  $getRoot,
} from 'lexical';
import {useEffect} from 'react';

import {$createPageNode, $isPageNode} from '../../nodes/PageNode';

function isEmptyParagraph(node) {
  return (
    $isParagraphNode(node) &&
    (node.getTextContent?.() ?? '').replace(/\u200B/g, '').trim() === ''
  );
}

function removeBodyPlaceholderIfEmpty(body) {
  if (!body) return;
  const kids = body.getChildren?.() ?? [];
  if (kids.length === 1 && isEmptyParagraph(kids[0])) {
    kids[0].remove();
  }
}

function removeLeadingEmptyParagraphIfFollowedByContent(body) {
  if (!body) return;
  const kids = body.getChildren?.() ?? [];
  if (kids.length <= 1) return;

  const first = kids[0];
  if (!isEmptyParagraph(first)) return;

  // Only remove if there is actual content after it.
  const restText = kids
    .slice(1)
    .map((n) => (n?.getTextContent?.() ?? '').replace(/\u200B/g, '').trim())
    .join('');

  if (restText === '') return;

  first.remove();
}

function getParentPage(node) {
  let n = node;
  while (n) {
    if ($isPageNode(n)) return n;
    n = n.getParent();
  }
  return null;
}

function getParentBody(node) {
  let n = node;
  while (n) {
    if (n.getType?.() === 'page_body') return n;
    n = n.getParent?.();
  }
  return null;
}

function getDirectChildOfBody(body, node) {
  if (!body || !node) return null;
  let n = node;
  while (n) {
    const parent = n.getParent?.();
    if (parent && parent.is && parent.is(body)) return n;
    if (parent === body) return n;
    if (parent == null) return null;
    n = parent;
  }
  return null;
}

function ensureNextPage(root, currentPage) {
  const pages = root.getChildren().filter($isPageNode);
  const idx = pages.findIndex((p) => p.getKey() === currentPage.getKey());
  const next = pages[idx + 1];
  if (next) return next;

  const newPage = $createPageNode();
  currentPage.insertAfter(newPage);
  return newPage;
}

function selectStart(node) {
  node.selectStart?.();
}

export const INSERT_PAGE_BREAK = createCommand();

function getPxCssVar(element, name, fallbackPx) {
  if (!element) return fallbackPx;
  const value = getComputedStyle(element).getPropertyValue(name).trim();
  if (!value) return fallbackPx;

  const match = value.match(/^(-?\d*\.?\d+)(px|mm|cm|in|pt|pc|rem|em)?$/i);
  if (!match) return fallbackPx;

  const amount = Number.parseFloat(match[1]);
  const unit = (match[2] || 'px').toLowerCase();
  if (!Number.isFinite(amount)) return fallbackPx;

  switch (unit) {
    case 'px':
      return amount;
    case 'mm':
      return (amount * 96) / 25.4;
    case 'cm':
      return (amount * 96) / 2.54;
    case 'in':
      return amount * 96;
    case 'pt':
      return (amount * 96) / 72;
    case 'pc':
      return (amount * 96) / 6;
    case 'em': {
      const fontSize = Number.parseFloat(getComputedStyle(element).fontSize || '16') || 16;
      return amount * fontSize;
    }
    case 'rem': {
      const root = document.documentElement;
      const fontSize =
        Number.parseFloat(getComputedStyle(root).fontSize || '16') || 16;
      return amount * fontSize;
    }
    default:
      return fallbackPx;
  }
}

export default function PageBreakPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        INSERT_PAGE_BREAK,
        () => {
          const selection = $getSelection();

          if (!$isRangeSelection(selection)) {
            return false;
          }

          const focusNode = selection.focus.getNode();
          const page = getParentPage(focusNode);
          if (!page) return false;

          const body = getParentBody(focusNode);
          if (!body) return false;

          const root = $getRoot();
          const nextPage = ensureNextPage(root, page);
          const nextBody = nextPage.getBody?.();
          if (!nextBody) return false;

          const block = getDirectChildOfBody(body, focusNode);
          if (!block) {
            // Fallback: just move caret to the start of the next page.
            selectStart(nextBody);
            return true;
          }

          // Move the current block and all following siblings to the next page body.
          // New pages are created with a placeholder empty paragraph; remove it so we
          // don't introduce an extra blank line before moved content.
          removeBodyPlaceholderIfEmpty(nextBody);

          let current = block;
          while (current) {
            const toMove = current;
            const next = current.getNextSibling?.();
            toMove.remove();
            nextBody.append(toMove);
            current = next;
          }

          // If the split happened at an empty paragraph that also got moved, and
          // there is real content after it, drop that empty paragraph so the new
          // page doesn't start with an extra blank line.
          removeLeadingEmptyParagraphIfFollowedByContent(nextBody);

          // Place caret at start of moved content.
          const firstMoved = nextBody.getFirstChild();
          if (firstMoved) {
            selectStart(firstMoved);
          } else {
            selectStart(nextBody);
          }

          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    );
  }, [editor]);

  return null;
}
